/**
 * Recorder (m-DVR) health (ADR 0027): camera channel and storage states reported over JT/T 808 (0xEF, 0x15),
 * kept per device, and alarms when a problem persists. The debounce runs in SQL
 * (supabase/migrations/20270503000001_device_health.sql, fts_device_health_report); this module holds the
 * shared rules, labels and a pure copy of the debounce (demo mode and tests). Pure TypeScript.
 *
 * KVKK: device states only — no person, no card, no position. ADR 0003: nothing here acts on a vehicle.
 */
import type { AlarmType } from "./alarms.js";

/** 0 normal and recording, 1 normal without recording, 2 disabled, 3 abnormal. */
export const DVR_CAMERA_STATES = ["recording", "not_recording", "disabled", "abnormal"] as const;
export type DvrCameraState = (typeof DVR_CAMERA_STATES)[number];
/** 0 normal, 1 disabled, 2 absent, 3 full without overwrite, 4 unformatted, 5 write error. */
export const DVR_STORAGE_STATES = ["normal", "disabled", "absent", "full", "unformatted", "write_error"] as const;
export type DvrStorageState = (typeof DVR_STORAGE_STATES)[number];
export const DVR_MEDIA = ["ssd", "sd", "usb", "usb2"] as const;
export type DvrMedium = (typeof DVR_MEDIA)[number];

export const DEVICE_HEALTH_LIMITS = Object.freeze({
  /** A problem raises an alarm only after it has been reported continuously for this long. */
  raiseAfterS: 600,
  /** A problem clears only after "OK" has been reported continuously for this long (no flapping). */
  clearAfterS: 120,
  /** Reports further apart than this break the continuity: the problem starts counting again. */
  maxGapS: 900,
  /** Defaults for a bridged m-DVR (ADR 0023): recording medium SD, cameras 1–4 installed. */
  defaultMedium: "sd" as DvrMedium,
  defaultCameras: [1, 2, 3, 4] as readonly number[],
  maxCameraChannel: 16,
  /** The console marks a problem whose status has not been reported for this long as "not current". */
  staleAfterS: 1800,
});

/** Storage states that raise "dvr_storage": absent, full without overwrite, unformatted, write error. */
export const DVR_STORAGE_PROBLEMS: readonly number[] = [2, 3, 4, 5];

export const DEVICE_HEALTH_ALARM_TYPES = ["dvr_storage", "dvr_camera"] as const satisfies readonly AlarmType[];

export function cameraStateName(s: number | null | undefined): DvrCameraState | "unknown" {
  return s !== null && s !== undefined && s >= 0 && s < DVR_CAMERA_STATES.length ? DVR_CAMERA_STATES[s]! : "unknown";
}
export function storageStateName(s: number | null | undefined): DvrStorageState | "unknown" {
  return s !== null && s !== undefined && s >= 0 && s < DVR_STORAGE_STATES.length ? DVR_STORAGE_STATES[s]! : "unknown";
}

/** Is a storage state a problem for the expected medium? */
export function storageProblem(state: number | null | undefined): boolean {
  return state !== null && state !== undefined && DVR_STORAGE_PROBLEMS.includes(state);
}

/**
 * Is an INSTALLED camera channel a problem? Anything but "normal and recording" (not recording, disabled,
 * abnormal), or its video signal is lost (0x15). Channels that are not installed are never a problem.
 */
export function cameraProblem(state: number | null | undefined, videoLost: boolean): boolean {
  if (state === null || state === undefined) return videoLost;
  return state !== 0 || videoLost;
}

/** Device settings (null = the default for the kind of device). */
export type DvrSettings = { medium: DvrMedium | null; cameras: number[] | null };

/**
 * Effective settings like fts_dvr_effective(): a bridged m-DVR (no card reader, ADR 0023) defaults to SD
 * and cameras 1–4; any other device has no recorder (nothing is evaluated) unless set explicitly.
 */
export function effectiveDvrSettings(s: DvrSettings, isBridgedDvr: boolean): { medium: DvrMedium | null; cameras: number[] } {
  return {
    medium: s.medium ?? (isBridgedDvr ? DEVICE_HEALTH_LIMITS.defaultMedium : null),
    cameras: [...(s.cameras ?? (isBridgedDvr ? DEVICE_HEALTH_LIMITS.defaultCameras : []))].sort((a, b) => a - b),
  };
}

/** One tracked problem: `storage` or `cam<n>`. */
export type HealthIssue = { key: string; state: number; badSince: number; lastAt: number; okSince: number | null; alarmed: boolean };

/** What a report observed: per key, problem or not (keys not observed are dropped). */
export type HealthObservation = { key: string; state: number; bad: boolean };

/**
 * The debounce (same rules as SQL, fts_device_health_report): a problem raises once after raiseAfterS of
 * continuous reports; it clears after clearAfterS of continuous OK reports; a gap longer than maxGapS
 * restarts the count. Storage is the primary alarm: no camera alarm while a storage problem is tracked, and
 * when it clears the camera problems not yet alarmed count again from that moment. Problems this report did
 * not observe are kept; `configured` drops the keys that are no longer configured (medium / channels).
 * Returns the new issue list and the keys to raise an alarm for now.
 */
export function healthStep(
  issues: readonly HealthIssue[],
  obs: readonly HealthObservation[],
  at: number,
  configured?: (key: string) => boolean,
): { issues: HealthIssue[]; raise: string[] } {
  const L = DEVICE_HEALTH_LIMITS;
  let list: HealthIssue[] = issues.filter((i) => !configured || configured(i.key)).map((i) => ({ ...i }));
  const raise: string[] = [];
  const ordered = [...obs].sort((a, b) => (a.key === "storage" ? -1 : b.key === "storage" ? 1 : 0));
  for (const o of ordered) {
    const cur = list.find((i) => i.key === o.key);
    if (o.bad) {
      const fresh = !cur || at - cur.lastAt > L.maxGapS * 1000;
      const next: HealthIssue = fresh
        ? { key: o.key, state: o.state, badSince: at, lastAt: at, okSince: null, alarmed: false }
        : { ...cur, state: o.state, lastAt: Math.max(cur.lastAt, at), okSince: null };
      const storageOpen = o.key !== "storage" && list.some((i) => i.key === "storage");
      if (!next.alarmed && !storageOpen && at - next.badSince >= L.raiseAfterS * 1000) {
        next.alarmed = true;
        raise.push(o.key);
      }
      list = [...list.filter((i) => i.key !== o.key), next];
    } else if (cur) {
      if (at - cur.lastAt > L.maxGapS * 1000 || (cur.okSince !== null && at - cur.okSince >= L.clearAfterS * 1000)) {
        list = list.filter((i) => i.key !== o.key);
        if (o.key === "storage") list = list.map((i) => (i.key.startsWith("cam") && !i.alarmed ? { ...i, badSince: at } : i));
      } else {
        list = list.map((i) => (i.key === o.key ? { ...i, okSince: i.okSince ?? at, lastAt: Math.max(i.lastAt, at) } : i));
      }
    }
  }
  return { issues: list.sort((a, b) => a.key.localeCompare(b.key)), raise };
}

/** What one report carries (any part may be missing: e.g. 0x15 without 0xEF). */
export type HealthReport = { cameras?: number[] | null; storage?: Partial<Record<DvrMedium, number>> | null; videoLoss?: number | null };

/** The stored state after a report, like SQL: a part the report does not carry keeps its last value. */
export function mergeHealthReport(prev: HealthReport | null, r: HealthReport): HealthReport {
  return {
    cameras: r.cameras ?? prev?.cameras ?? null,
    storage: r.storage ?? prev?.storage ?? null,
    videoLoss: r.videoLoss ?? prev?.videoLoss ?? null,
  };
}

/**
 * The observations of one report for the effective settings. Storage is observed only when the report
 * carries it; cameras when the report carries camera states or video loss, judged on the latest KNOWN
 * states (`prev` = the stored state before this report), so a 0x15-only report neither clears nor invents a
 * camera problem.
 */
export function healthObservations(r: HealthReport, eff: { medium: DvrMedium | null; cameras: number[] }, prev: HealthReport | null = null): HealthObservation[] {
  const obs: HealthObservation[] = [];
  const st = eff.medium && r.storage ? r.storage[eff.medium] : undefined;
  if (eff.medium && st !== undefined && st !== null) obs.push({ key: "storage", state: st, bad: storageProblem(st) });
  const m = mergeHealthReport(prev, r);
  const carries = (r.cameras !== undefined && r.cameras !== null) || (r.videoLoss !== undefined && r.videoLoss !== null);
  const known = !!m.cameras || (m.videoLoss !== undefined && m.videoLoss !== null);
  if (carries && known) {
    for (const ch of eff.cameras) {
      const s = m.cameras?.[ch - 1];
      const lost = m.videoLoss !== undefined && m.videoLoss !== null ? ((m.videoLoss >>> (ch - 1)) & 1) === 1 : false;
      obs.push({ key: `cam${ch}`, state: s ?? -1, bad: cameraProblem(s, lost) });
    }
  }
  return obs;
}

/** Keys still configured for the effective settings (problems of other keys end at once). */
export function healthConfigured(eff: { medium: DvrMedium | null; cameras: number[] }): (key: string) => boolean {
  return (key) => (key === "storage" ? eff.medium !== null : key.startsWith("cam") && eff.cameras.includes(Number(key.slice(3))));
}

/** Recorder health of one device as the console sees it. */
export type DeviceHealth = {
  deviceId: string;
  vehicleId: string | null;
  /** null = the device has not reported recorder states (for its current vehicle). */
  reportedAt: string | null;
  /** Camera channel states 1–16 (index 0 = channel 1). */
  cameras: number[];
  ipcs: number[];
  storage: Partial<Record<DvrMedium, number>>;
  /** Video-loss bitmap (bit 0 = channel 1); null when not reported. */
  videoLoss: number | null;
  settings: DvrSettings;
  effective: { medium: DvrMedium | null; cameras: number[] };
  /** Open problems: key, state, since, and whether an alarm was raised. */
  /** `lastAt`: the last report that carried this problem's status (older than staleAfterS = not current). */
  issues: { key: string; state: number; since: string; lastAt: string; alarmed: boolean }[];
};
